import { translate } from "app/filters/i18nFilter";
import type { ChipItem } from "app/pages/sales-intelligence/common-components/filters-panel";
import type {
    IndustryNodeId,
    IndustryTaxonomyIndex,
} from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import type { CrmScopeOption, IKeywordsFilterDto } from "app/pages/sales-intelligence/domain/leads/types/filter-values";
import type { DynamicFilterValue } from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-values";
import { parseDynamicFilterValue } from "app/pages/sales-intelligence/domain/search/utils/dynamic-filter-values";
import { isFilterValueEmpty } from "app/pages/sales-intelligence/domain/search/utils/is-filter-value-empty";
import type { TechnologiesFilterDto } from "app/pages/sales-intelligence/domain/technology/types/filter-values";
import { translateOption } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/filters/RelativeDateRangeFilter/utils";
import CountryService from "app/services/CountryService";

import { mappingValueToLabel } from "../components/filters/EmployeeHeadcountFilter/utils/mappingValueToLabel";
import type { ILeadsSearchFiltersDto } from "../types/leadsSearchFilters.dto";

import leadsSearchFiltersI18nKeys from "./i18n";
import { getLeadScoreFilterOptionLabel } from "./leadScoreFilterLabels";
import { leadsFilterDtoToPanelValue } from "./leadsFilterDtoAdapters";
import { formatFloatingNumber, formatMoney, formatNumber, formatPercent, formatTime } from "./numberFormatters";

/**
 * Read-only preview of a saved leads-filter set, as "<Filter name>: <value>" chips.
 *
 * Names come from the filters config (`titleByKey`) — the single source of truth the Filters
 * tab uses — so labels stay correct/complete for the configured filter sections without
 * hardcoding i18n keys. Values are rendered here with a tiered fallback, because the per-filter
 * value→text logic lives in the React result components (no pure function to reuse):
 *
 *   1. Resolvable  → real value(s): countries, ranges, enums, technologies, dynamic CRM fields, strings.
 *   2. Opaque list → "<Name>: <count>": items we can't name without loaded options/logic (profiles, etc.).
 *   3. Set, neither → "<Name>" name-only.
 *
 * We never render a raw id or i18n key. A filter with no resolved title is skipped (it appears
 * once the config loads), so chips are inherently scoped to whatever sections the config exposes.
 */

type TitleByKey = ReadonlyMap<string, string>;

export const COUNTRY_LIST_KEYS: ReadonlySet<string> = new Set(["countries", "topGeoCountries"]);
const ENUM_ARRAY_KEYS = new Set<string>(["audienceAgeGroups", "audienceGenderSplits", "businessModels", "leadScores"]);
const SINGLE_RANGE_KEYS = new Set<string>([
    "annualRevenue",
    "onlineRevenue",
    "conversionRate",
    "monthlyAverageTransactions",
    "totalFundingAmount",
    "usdSpend",
]);

type Range = { from?: number; to?: number };

type NumberFormatter = (value: number) => string;

const FIELD_FORMATTER: Record<string, NumberFormatter> = {
    annualRevenue: formatMoney,
    onlineRevenue: formatMoney,
    totalFundingAmount: formatMoney,
    usdSpend: formatMoney,
    conversionRate: formatPercent,
    bounceRate: formatPercent,
    avgVisitDuration: formatTime,
    avgVisitsPerUser: (value) => String(formatFloatingNumber(value)),
};

const getFieldFormatter = (field: string): NumberFormatter => {
    if (FIELD_FORMATTER[field]) {
        return FIELD_FORMATTER[field];
    }
    // Every "*Share" metric (traffic mix, marketing-channel shares, …) is a percentage.
    if (field.endsWith("Share")) {
        return formatPercent;
    }
    return formatNumber;
};

const formatRange = (range: Range, format: NumberFormatter): string | null => {
    const { from, to } = range;
    if (from != null && to != null) {
        return `${format(from)}–${format(to)}`;
    }
    if (from != null) {
        return `≥ ${format(from)}`;
    }
    if (to != null) {
        return `≤ ${format(to)}`;
    }
    return null;
};

const isRange = (value: unknown): value is Range => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }
    const { from, to } = value as Range;
    return typeof from === "number" || typeof to === "number";
};

/**
 * A composite filter value is an object whose every set property is itself a numeric range
 * (e.g. Incoming traffic → { monthlyVisits, avgMonthlyVisits, … }). Returns the set sub-ranges,
 * or `null` when the value isn't a pure range-of-ranges (so mixed objects fall through untouched).
 */
const toSubRanges = (value: unknown): [string, Range][] | null => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return null;
    }
    const entries = Object.entries(value).filter(([, sub]) => sub != null);
    if (entries.length === 0 || !entries.every(([, sub]) => isRange(sub))) {
        return null;
    }
    return entries as [string, Range][];
};

const countryNames = (codes: readonly number[]): string[] =>
    codes.map((code) => CountryService.getCountryById(code)?.text ?? String(code));

const isStringArray = (value: unknown): value is readonly string[] =>
    Array.isArray(value) && value.every((item) => typeof item === "string");

/** Translate an enum option, falling back to the raw value rather than surfacing the key. */
const translateOr = (key: string, fallback: string): string => {
    const label = translate(key);
    return label === key ? fallback : label;
};

/**
 * A location DTO stores region *names* ("EMEA"); expanding them into their countries needs the loaded
 * region options this key-only renderer has no access to, so name the region itself. The label key
 * mirrors `RegionService`, and the raw name is already readable when it has no translation.
 */
const regionNames = (names: readonly string[]): string[] =>
    names.map((name) => translateOr(`si.common.region.${name}.label`, name));

/** A composite sub-range rendered as "<sub-metric label> <formatted range>", or null when empty. */
const labeledRange = (field: string, range: Range): string | null => {
    const text = formatRange(range, getFieldFormatter(field));
    return text ? `${translateOr(leadsSearchFiltersI18nKeys.filterChip(field), field)} ${text}` : null;
};

/**
 * A dynamic (backend-described) field value — CRM custom fields. Enum ids are surfaced raw: their labels
 * live on the field's loaded description, which this key-only renderer has no access to.
 */
const dynamicValues = (key: string, value: DynamicFilterValue): string[] => {
    switch (value.dataType) {
        case "enum":
            return [value.value];
        case "enumArray":
            return [...value.value];
        case "date": {
            const { range, rangeType } = value.value;

            return [`${translateOption(rangeType)} ${translateOption(range)}`];
        }
        case "numberRange": {
            const text = formatRange(value.value, getFieldFormatter(key));

            return text ? [text] : [];
        }
    }
};

type RenderedValue = { kind: "values"; values: string[] } | { kind: "count"; count: number } | { kind: "nameOnly" };

const toValues = (values: string[]): RenderedValue => ({ kind: "values", values });

/**
 * Taxonomy ids are opaque, so a name needs the loaded taxonomy. Anything short of naming *every*
 * selected node falls back to the count tier: a partial list reads as complete, and the raw id is
 * exactly what this file promises never to render.
 */
const renderIndustryTaxonomy = (ids: readonly IndustryNodeId[], taxonomy?: IndustryTaxonomyIndex): RenderedValue => {
    const names = taxonomy
        ? ids.map((id) => taxonomy.byId.get(id)?.name).filter((name): name is string => Boolean(name))
        : [];

    return names.length === ids.length ? toValues(names) : { kind: "count", count: ids.length };
};

/** Classify a single filter's DTO value into displayable text (see tiers in the file header). */
const renderValue = (key: string, value: unknown, taxonomy?: IndustryTaxonomyIndex): RenderedValue => {
    if (COUNTRY_LIST_KEYS.has(key) && Array.isArray(value)) {
        return toValues(countryNames(value as readonly number[]));
    }

    if (key === "industryFilter" && value && typeof value === "object") {
        const dto = value as { values?: readonly { mainCategory: string; subCategory?: string }[] };
        return toValues((dto.values ?? []).map((item) => item.subCategory ?? item.mainCategory));
    }

    if (key === "industryTaxonomy" && value && typeof value === "object") {
        const dto = value as { values?: readonly IndustryNodeId[] };
        return renderIndustryTaxonomy(dto.values ?? [], taxonomy);
    }

    if (key === "industryCodesFilter" && Array.isArray(value)) {
        return toValues(
            (value as readonly { sicCodes?: readonly string[]; naicsCodes?: readonly string[] }[]).flatMap((item) => [
                ...(item.naicsCodes ?? []),
                ...(item.sicCodes ?? []),
            ]),
        );
    }

    if (key === "siteTags" && Array.isArray(value)) {
        return toValues((value as readonly { values?: readonly string[] }[]).flatMap((item) => item.values ?? []));
    }

    if (key === "companyHeadquarter" && value && typeof value === "object") {
        const dto = value as { codes?: readonly number[]; regions?: readonly string[]; zip?: readonly string[] };
        return toValues([...regionNames(dto.regions ?? []), ...countryNames(dto.codes ?? []), ...(dto.zip ?? [])]);
    }

    // Employee headcount values are range codes ("range_1000_5000"), not translatable enum keys.
    if (key === "employeeHeadcount" && isStringArray(value)) {
        return toValues(value.map(mappingValueToLabel));
    }

    // Technology conditions carry plain category/technology names (the chip flattens the same three
    // lists), so name them instead of falling through to the opaque-array count.
    if (key === "technologies" && Array.isArray(value)) {
        return toValues(
            (value as readonly TechnologiesFilterDto[]).flatMap((item) => [
                ...(item.parentCategories ?? []),
                ...(item.categories ?? []),
                ...(item.technologies ?? []),
            ]),
        );
    }

    const dynamicValue = parseDynamicFilterValue(value);
    if (dynamicValue) {
        return toValues(dynamicValues(key, dynamicValue));
    }

    // Website type carries its selected types under `.types` (plus an inclusion mode we don't surface).
    if (key === "websiteType" && value && typeof value === "object") {
        const types = (value as { types?: readonly string[] }).types ?? [];
        return toValues(types.map((type) => translateOr(`si.lead_gen_filters.websiteType.${type}`, type)));
    }

    // Keywords is a single search term plus the traffic source it was seen on. Only the term is
    // rendered — same as the filters panel — and a value carrying just a traffic source names nothing.
    if (key === "keywords" && value && typeof value === "object") {
        const { keyword } = value as IKeywordsFilterDto;

        return toValues(keyword ? [keyword] : []);
    }

    // International visits is a mixed composite: a country plus visit/share sub-ranges (not a pure
    // range-of-ranges, so `toSubRanges` skips it). Render the country name then each set sub-range.
    if (key === "internationalFilters" && value && typeof value === "object") {
        const dto = value as {
            internationalCountry?: number;
            internationalVisits?: Range;
            internationalVisitsShare?: Range;
        };
        const values: string[] = dto.internationalCountry != null ? countryNames([dto.internationalCountry]) : [];
        for (const field of ["internationalVisits", "internationalVisitsShare"] as const) {
            const range = dto[field];
            const line = range ? labeledRange(field, range) : null;
            if (line) {
                values.push(line);
            }
        }
        return toValues(values);
    }

    if (key === "leadScores" && isStringArray(value)) {
        return toValues(value.map((item) => getLeadScoreFilterOptionLabel(item as never)));
    }

    if (key === "crmScope" && typeof value === "string") {
        return toValues([translateOr(leadsSearchFiltersI18nKeys.crmScopeOptionLabel(value as CrmScopeOption), value)]);
    }

    if (ENUM_ARRAY_KEYS.has(key) && isStringArray(value)) {
        return toValues(value.map((item) => translateOr(`si.lead_gen_filters.${key}.${item}`, item)));
    }

    if (SINGLE_RANGE_KEYS.has(key) && value && typeof value === "object") {
        const text = formatRange(value as Range, getFieldFormatter(key));
        return text ? toValues([text]) : { kind: "nameOnly" };
    }

    // Composite range filters (e.g. incomingTraffic, engagement, marketingChannels): the value is an
    // object of named sub-ranges — render each set sub-range under its own "<sub-metric> <range>" label.
    const subRanges = toSubRanges(value);
    if (subRanges) {
        return toValues(
            subRanges
                .map(([field, range]) => labeledRange(field, range))
                .filter((line): line is string => line !== null),
        );
    }

    if (isStringArray(value)) {
        return toValues([...value]);
    }

    if (value && typeof value === "object" && isStringArray((value as { values?: unknown }).values)) {
        return toValues([...(value as { values: readonly string[] }).values]);
    }

    // Arrays of opaque items (objects / ids needing options) → count.
    if (Array.isArray(value)) {
        return { kind: "count", count: value.length };
    }

    if (typeof value === "string") {
        return toValues([value]);
    }

    if (typeof value === "number") {
        return toValues([formatNumber(value)]);
    }

    // A set object we can't break down meaningfully → just the name.
    return { kind: "nameOnly" };
};

const toChips = (key: string, name: string, rendered: RenderedValue): ChipItem[] => {
    if (rendered.kind === "values") {
        return rendered.values
            .filter((value) => value.trim().length > 0)
            .map((value, index) => ({ id: `${key}-${index}`, text: `${name}: ${value}` }));
    }
    if (rendered.kind === "count") {
        return rendered.count > 0 ? [{ id: key, text: `${name}: ${rendered.count}` }] : [];
    }
    return [{ id: key, text: name }];
};

/**
 * One summary line per set filter, with the filter `name` kept separate from its `value` so callers
 * can style them independently (e.g. bold the name in a tooltip). `value` is empty for name-only filters.
 */
export type FilterSummaryLine = { id: string; name: string; value: string };

/** Shared "+N more" overflow label key — a generic, namespace-agnostic count string. */
const MORE_toValues_I18N_KEY = "si.management.teams.card.filter_chips.more";

const DEFAULT_VALUE_SEPARATOR = ", ";

/**
 * Join a filter's values, capping the list at `maxValuesPerLine` and appending a "+N more"
 * label for the remainder, so a single multi-select filter can't blow up a compact preview.
 */
const joinValues = (values: string[], maxValuesPerLine?: number, separator = DEFAULT_VALUE_SEPARATOR): string => {
    if (maxValuesPerLine == null || values.length <= maxValuesPerLine) {
        return values.join(separator);
    }
    const visible = values.slice(0, maxValuesPerLine);
    const overflow = values.length - visible.length;
    return `${visible.join(separator)}${separator}${translate(MORE_toValues_I18N_KEY, { count: overflow })}`;
};

const makeSummaryLineBuilder =
    (maxValuesPerLine?: number, separator?: string) =>
    (key: string, name: string, rendered: RenderedValue): FilterSummaryLine[] => {
        if (rendered.kind === "values") {
            const values = rendered.values.filter((value) => value.trim().length > 0);
            return values.length > 0 ? [{ id: key, name, value: joinValues(values, maxValuesPerLine, separator) }] : [];
        }
        if (rendered.kind === "count") {
            return rendered.count > 0 ? [{ id: key, name, value: String(rendered.count) }] : [];
        }
        return [{ id: key, name, value: "" }];
    };

/** One displayable entry (or several) per set filter — `toEntries` decides chip-vs-summary shaping. */
type EntryBuilder<TEntry> = (key: string, name: string, rendered: RenderedValue) => TEntry[];

/**
 * Walk a saved filter set into displayable entries: drop empty values, resolve each filter's title
 * from the config (`titleByKey`), classify its value via {@link renderValue}, then hand off to
 * `toEntries` for the per-mode shaping ({@link toChips} vs {@link toSummaryLine}).
 */
const buildEntries = <TEntry>(
    filters: Partial<ILeadsSearchFiltersDto> | undefined,
    titleByKey: TitleByKey,
    toEntries: EntryBuilder<TEntry>,
    taxonomy?: IndustryTaxonomyIndex,
): TEntry[] => {
    if (!filters) {
        return [];
    }

    const panelValue = leadsFilterDtoToPanelValue(filters) as Record<string, unknown>;

    return Object.entries(panelValue)
        .filter(([, value]) => !isFilterValueEmpty(value))
        .flatMap(([key, value]) => {
            const name = titleByKey.get(key);
            // No resolved title → skip rather than render a raw key (it appears once config loads).
            return name ? toEntries(key, name, renderValue(key, value, taxonomy)) : [];
        });
};

/**
 * Per-value chips (one chip per value) for the filters panel.
 * Pass `taxonomy` to name industry-taxonomy nodes; without it they degrade to a count.
 */
export const buildLeadsFilterChips = (
    filters: Partial<ILeadsSearchFiltersDto> | undefined,
    titleByKey: TitleByKey,
    taxonomy?: IndustryTaxonomyIndex,
): ChipItem[] => buildEntries(filters, titleByKey, toChips, taxonomy);

/**
 * One joined line per filter for compact previews (e.g. team-card territory summary) and exports.
 * Pass `maxValuesPerLine` to cap a single filter's values with a trailing "+N more" label, and
 * `separator` when the consumer needs something other than a comma between values (e.g. "; " for Excel).
 */
export const buildLeadsFilterSummary = (
    filters: Partial<ILeadsSearchFiltersDto> | undefined,
    titleByKey: TitleByKey,
    maxValuesPerLine?: number,
    separator?: string,
    taxonomy?: IndustryTaxonomyIndex,
): FilterSummaryLine[] =>
    buildEntries(filters, titleByKey, makeSummaryLineBuilder(maxValuesPerLine, separator), taxonomy);
